import React from 'react';

import { Tooltip } from '@components';
import { DonutChart } from '@components/index';
import useIsTruncated from '@hooks/useIsTruncated';

import AnalyticsCardState from './AnalyticsCardState';
import AnalyticsViewSelector from './AnalyticsViewSelector';

import { LeadAnalyticsCriteria } from '../../constants/leadAnalytics';
import { LeadSource } from '../../types/leadAnalytics';
import { ANALYTICS_TOOLTIP_STYLE } from '../../utils/analyticsColors';

const LEGEND_MAX_VISIBLE_ROWS = 7;

const getLegendColumns = (sources: LeadSource[]): LeadSource[][] => {
  if (!sources.length) return [];
  const columnSize = Math.max(
    LEGEND_MAX_VISIBLE_ROWS,
    Math.ceil(sources.length / 2)
  );
  return sources.length > columnSize
    ? [sources.slice(0, columnSize), sources.slice(columnSize)]
    : [sources];
};

interface LeadsDistributionCardProps {
  sources: LeadSource[];
  view: LeadAnalyticsCriteria;
  onViewChange: (view: LeadAnalyticsCriteria) => void;
  isLoading?: boolean;
  className?: string;
}

const SourceLegendItem: React.FC<{ source: LeadSource }> = ({ source }) => {
  const { ref: textRef, isTruncated } = useIsTruncated<HTMLSpanElement>(
    source.label
  );

  return (
    <div
      className="flex min-w-0 items-center gap-2"
      data-tooltip-id={`source-legend-${source.label}`}
    >
      <span
        style={{ background: source.color }}
        className="inline-block size-2 shrink-0 rounded-full"
      />
      <span
        ref={textRef}
        className="truncate text-[14px] font-normal text-content"
      >
        {source.label}
      </span>
      <span className="shrink-0 text-[14px] font-normal text-content">
        – {source.value}%
      </span>
      {isTruncated && (
        <Tooltip
          id={`source-legend-${source.label}`}
          className="rounded-lg !bg-cofeeNeutral10"
        >
          <div className="max-w-xs whitespace-normal break-words px-2 py-1 text-sm text-cofeeNeutral1">
            {source.label}
          </div>
        </Tooltip>
      )}
    </div>
  );
};

const LeadsDistributionCard: React.FC<LeadsDistributionCardProps> = ({
  sources = [],
  view,
  onViewChange,
  isLoading = false,
  className
}) => {
  const chartSeries = sources?.map((s) => ({ value: s.value, count: s.count }));
  const chartLabels = sources?.map((s) => s.label);
  const chartColors = sources?.map((s) => s.color);
  const legendColumns = getLegendColumns(sources);
  const hasData = sources?.some((source) => source.count > 0);

  return (
    <div
      className={`flex min-h-90 w-full min-w-70 flex-1 flex-col justify-between rounded-xl border border-grey10 bg-white p-6 shadow-md ${
        className || ''
      }`}
    >
      <div className="mb-2 flex items-center justify-between gap-4">
        <div className="text-20 font-semibold text-content">
          Leads By Source
        </div>
        <AnalyticsViewSelector
          dropdownId="leads-source-view"
          view={view}
          onViewChange={onViewChange}
        />
      </div>
      <AnalyticsCardState
        isLoading={isLoading}
        hasData={hasData}
        emptyCardType="donutChart"
      >
        <div className="flex flex-1 flex-row gap-6">
          <div className="flex flex-1 items-center justify-center">
            <DonutChart
              series={chartSeries}
              labels={chartLabels}
              colors={chartColors}
              width={257}
              height={257}
              customOptions={{
                tooltip: {
                  trigger: 'item',
                  ...ANALYTICS_TOOLTIP_STYLE,
                  formatter: (p: { data: { name: string; count: number } }) =>
                    `${p.data.name} • ${p.data.count} Leads`
                }
              }}
            />
          </div>
          <div className="flex min-w-0 flex-1 items-center">
            <div className="custom-scroll flex max-h-[257px] w-full flex-wrap gap-x-6 gap-y-4 overflow-y-auto pr-2">
              {legendColumns.map((column) => (
                <div
                  key={column[0].label}
                  className="flex min-w-35 flex-1 flex-col gap-4"
                >
                  {column.map((source) => (
                    <SourceLegendItem key={source.label} source={source} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </AnalyticsCardState>
    </div>
  );
};

export default LeadsDistributionCard;
